import { ListItemIcon, Button, FormControl, Stack, TextField, Typography, Tooltip, Box, InputAdornment, Select, MenuItem, Divider, SelectChangeEvent } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { User } from '@/_models/User';
import { DescriptionField } from './NoteDescriptionField';
import { SAPNoteForm } from './SAPNoteForm';
import Icon from '@/units/Icon';
import { CircularProgress } from '@mui/material';

export const noteOptions = [
  { value: 'message', label: 'Leave a message' },
  { value: 'SAP', label: 'SAP report' },
];

const maxFilesSize = 20971520;

export const NoteForm = ({
  noteFormat,
  setNoteFormat,
  files,
  setFiles,
  handleAdd,
  editMode,
  setEditMode,
  mediaUrls,
  setMediaUrls,
  userId,
  rtl,
  isNewThread,
  collaborators,
  foundUser,
  isMainMessage,
  SAPData,
  setSAPData,
  onGetSAPOptions,
  isSAPActive,
  noteTitle,
  currentMessage,
  onCloseDrawer,
}: {
  files: File[];
  noteFormat: string;
  setNoteFormat: (noteFormat: string) => void;
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  handleAdd: ({}) => Promise<string>;
  isNewThread: boolean;
  editMode?: boolean;
  setEditMode?: (editMode: boolean) => void;
  mediaUrls?: { src: string; flaggedToDelete: boolean }[];
  setMediaUrls?: (state: any) => void;
  userId?: null | number;
  rtl: boolean;
  collaborators: User[];
  foundUser?: User;
  isMainMessage?: boolean;
  SAPData?: any;
  setSAPData?: (data: any) => void;
  onGetSAPOptions?: () => Promise<any>;
  isSAPActive?: boolean;
  noteTitle?: string;
  currentMessage?: any;
  onCloseDrawer: () => void;
}) => {
  const [assignee, setAssignee] = useState('');
  const [description, setDescription] = useState(currentMessage?.description || '');
  const [title, setTitle] = useState(noteTitle || '');
  const [focused, setFocused] = useState(false);
  const [previewSrcs, setPreviewSrcs] = useState<{ name?: string; lastModified?: number; src?: string }[]>([]);
  const [audioURLs, setAudioURLs] = useState<{ lastModified?: number; src?: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [SAPOptions, setSAPOptions] = useState([]);
  const [descriptionLength, setDescriptionLength] = useState<number>(0);
  const [loadingFiles, setLoadingFiles] = useState([]);
  const [messageId, setMessageId] = useState<number | null>(currentMessage?.id || null);
  const [hasShownError, setHasShownError] = useState(false);

  const [titleError, setTitleError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const inputRef = useRef(null);
  const taggingEl = useRef(null);

  const clearInput = () => {
    setDescription('');
    setTitle('');
    setNoteFormat('message');
    setFocused(false);
    setFiles([]);
    setPreviewSrcs([]);
    setAudioURLs([]);
    setIsLoading(false);
    setMessageId(null);
    setHasShownError(false);
    setSAPData({});
    setAssignee(null);
    if (setEditMode) {
      setEditMode(false);
    }
  };

  const handleChangeTitle = (e) => {
    const value = e.target.value;
    if (value) {
      setTitleError('');
    }
    if (userId) {
      setTitle(value);
    }
  };

  const handleClickOutside = (event) => {
    if (inputRef.current && !inputRef.current.contains(event.target) && taggingEl.current && !taggingEl.current.contains(event.target)) {
      setFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (description) {
      setFocused(true);
    }

    if (descriptionLength > 300 && !hasShownError) {
      toast.error('Description must be no more than 300 characters');
      setHasShownError(true);
    }

    return () => setFocused(false);
  }, [description, descriptionLength, hasShownError]);

  useEffect(() => {
    setAddButtonDisabled(isSubmitDisabled());
  }, [title, descriptionLength, mediaUrls, previewSrcs, audioURLs]);

  const isSubmitDisabled = () => {
    if (((isNewThread || isMainMessage) && title === '') || ((!descriptionLength || descriptionLength > 300) && !mediaUrls?.length && !previewSrcs?.length && !audioURLs?.length)) {
      if (((isNewThread || isMainMessage) && title === '')) {
        setTitleError('This field cannot be empty');
      }

      if (((!descriptionLength || descriptionLength > 300) && !mediaUrls?.length && !previewSrcs?.length && !audioURLs?.length)) {
        setDescriptionError('This field cannot be empty');
      }
      return true;
    }
    setDescriptionError('')
    return false;
  };

  const handleFileChange = (event) => {
    const uploadedFiles: File[] = Array.from(event.target.files);

    if (uploadedFiles.length) {
      const fileReaders = uploadedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const { type } = file;

          setLoadingFiles((prevLoading) => [...prevLoading, { name: file.name, lastModified: file.lastModified }]);

          if (type === 'image/png' || type === 'image/jpeg' || type === 'image/webp') {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = (e) => {
              resolve({
                name: file.name,
                lastModified: file.lastModified,
                src: e.target.result,
                type: 'image',
              });
            };

            reader.onerror = (e) => {
              reject(e);
            };
          } else if (type.startsWith('video/')) {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'video',
            });
          } else if (type === 'application/pdf') {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'pdf',
            });
          } else {
            toast.error('Unsupported file format');
            reject(new Error('Unsupported file format'));
          }
        });
      });

      Promise.allSettled(fileReaders).then((results) => {
        const successfulResults = results
          .filter((result): result is PromiseFulfilledResult<{ name: string; lastModified: number; src: string }> => result.status === 'fulfilled')
          .map((result) => result.value);

        const successfulFiles = uploadedFiles.filter((_, index) => results[index].status === 'fulfilled');

        setLoadingFiles([]);

        if (successfulResults.length) {
          setPreviewSrcs((prevSrcs) => [...prevSrcs, ...successfulResults]);
          setFiles((prevUploads) => [...prevUploads, ...successfulFiles]);
        }
      });

      event.target.value = null;
    }
  };

  const removeReview = ({ name, lastModified }: { name?: string; lastModified?: number }) => {
    const filteredPreviews = previewSrcs.filter((preview) => preview.name !== name && preview.lastModified !== lastModified);
    const filteredFiles = files.filter((file) => file.name !== name && file.lastModified !== lastModified);

    setPreviewSrcs(filteredPreviews);
    setFiles(filteredFiles);
  };

  const removeAudio = ({ lastModified }: { lastModified: number }) => {
    const filteredAudios = audioURLs.filter((audioUrl) => audioUrl.lastModified !== lastModified);
    const filteredFiles = files.filter((file) => file.lastModified !== lastModified);

    setAudioURLs(filteredAudios);
    setFiles(filteredFiles);
  };

  useEffect(() => {
    const uploadedFilesSize: number = files.reduce((acc, file) => acc + file.size, 0);

    if (files.length + mediaUrls?.length > 10) {
      setAddButtonDisabled(true);
      toast.error('Too many files, max count is 10');
    } else if (uploadedFilesSize > maxFilesSize) {
      setAddButtonDisabled(true);
      toast.error('Files are too large, max is 20mb');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    const fetchSAPOptions = async () => {
      setIsLoading(true);
      try {
        const options = await onGetSAPOptions();
        setSAPOptions(options);
      } catch (error) {
        console.error('Error fetching SAP options:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (noteFormat === 'SAP') {
      fetchSAPOptions();
    }
  }, [noteFormat]);

  useEffect(() => {
    return () => {
      clearInput();
    };
  }, []);

  const handleChangeType = async (event: SelectChangeEvent<string>) => {
    const format = event.target.value as string;
    setNoteFormat(format);
  };

  return (
    <>
      <FormControl sx={{ height: '100%', paddingTop: '18px' }}>
        {(isNewThread || editMode) && (
          <>
            {isNewThread && isSAPActive && (
              <>
                <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="10px">
                  <Typography variant="body1" sx={{ marginRight: 2, color: '#344054', fontSize: '14px', background: 'transparent' }}>
                    Type
                  </Typography>
                  <FormControl sx={{ minWidth: 200 }}>
                    <Select
                      labelId="noteOptions-label"
                      id="noteOptions"
                      value={noteFormat}
                      label="Select Type"
                      onChange={handleChangeType}
                      IconComponent={(props) => <Icon.ChevronDown sx={{ fontSize: '20px', marginRight: '7px' }} {...props} />}
                      sx={{
                        outline: '1px solid #D0D5DD',
                        backgroundColor: '#FFF',
                        borderRadius: '6px',
                        height: '44px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: '8px',
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'blue',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'blue',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            backgroundColor: '#FFF',
                          },
                        },
                      }}
                      renderValue={(selected) => {
                        const selectedOption = noteOptions.find((option) => option.value === selected);
                        return selectedOption ? selectedOption.label : '';
                      }}
                    >
                      {noteOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(130, 130, 130, 0.08)'
                              },
                            },
                          }}
                        >
                          <Typography sx={{mr: 'auto'}}>{option.label}</Typography>
                          {noteFormat === option.value && (
                            <ListItemIcon sx={{  minWidth: '0 !important' }}>
                              <i className="fa-light fa-check"  style={{ color: 'blue', fontSize: '20px'}}/>
                            </ListItemIcon>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Divider sx={{ mb: '10px' }} />
              </>
            )}
            {(isNewThread || isMainMessage) && (
              <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="space-between" marginBottom="10px">
                <Typography sx={{ color: '#344054', marginBottom: '8px', background: 'transparent', fontSize: '14px' }}>Title</Typography>
                <TextField
                  variant="outlined"
                  {...(editMode ? { defaultValue: title } : { value: title })}
                  placeholder="Write a title"
                  onChange={handleChangeTitle}
                  inputProps={{
                    style: {
                      color: 'black',
                      fontFamily: 'inherit',
                      height: '100%',
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: 400,
                    },
                  }}
                  InputProps={{
                    endAdornment: !!titleError ? (
                      <InputAdornment position="end" style={{ position: 'absolute', right: '14px', bottom: '50%' }}>
                        <i className="fa-regular fa-circle-exclamation" style={{ color: 'red', fontSize: '16px'}}/>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: '100%',
                    borderRadius: '8px',
                    border: !!titleError ? 'none' : '1px solid #D0D5DD',
                    margin: 0,
                    background: !!titleError ? 'transparent' : '#FFF',
                    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                    '& fieldset': { height: '100%', top: 0 },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D0D5DD',
                      borderRadius: '8px',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      flexDirection: 'column',
                      '&.Mui-error': {
                        borderColor: 'red',
                        background: '#FFF',
                      },
                    },
                    fontWeight: 400,

                  }}
                  InputLabelProps={{
                    style: {
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#667085',
                      opacity: 1,
                    },
                  }}
                  error={!!titleError}
                  helperText={titleError}
                />
              </Box>
            )}
          </>
        )}

        <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="space-between" marginBottom="10px">
          {(isNewThread || editMode) && (
            <Stack flexDirection="row" alignItems="center" gap={1} marginBottom={1}>
              <Typography sx={{ color: '#344054', fontSize: '14px', background: 'transparent' }}>Description</Typography>
              <Tooltip title={'tooltip'}>
                <i className="fa-light fa-circle-question" style={{ fontSize: '14px' }} />
              </Tooltip>
            </Stack>
          )}
          <DescriptionField
            focused={focused}
            setFocused={setFocused}
            foundUser={foundUser}
            isNewThread={isNewThread}
            editMode={editMode}
            setFiles={setFiles}
            addButtonDisabled={addButtonDisabled}
            setAddButtonDisabled={setAddButtonDisabled}
            setIsLoading={setIsLoading}
            handleAdd={handleAdd}
            clearInput={clearInput}
            description={description}
            setDescription={setDescription}
            setAudioURLs={setAudioURLs}
            previewSrcs={previewSrcs}
            loadingFiles={loadingFiles}
            audioURLs={audioURLs}
            mediaUrls={mediaUrls}
            isLoading={isLoading}
            removeReview={removeReview}
            removeAudio={removeAudio}
            setMediaUrls={setMediaUrls}
            handleFileChange={handleFileChange}
            collaborators={collaborators}
            descriptionLength={descriptionLength}
            setDescriptionLength={setDescriptionLength}
            descriptionError={descriptionError}
          />
        </Box>

        {noteFormat === 'SAP' && (
          <>
            <Divider sx={{ marginBottom: '10px' }} />
            {isLoading ? (
              <CircularProgress />
            ) : (
              <SAPNoteForm collaborators={collaborators} assignee={assignee} setAssignee={setAssignee} SAPData={SAPData} setSAPData={setSAPData} SAPFieldsData={SAPOptions} />
            )}
          </>
        )}

        {(isNewThread || editMode) && (
          <Stack direction="row" alignSelf="flex-end" paddingY="10px" marginTop={isNewThread ? 'auto' : 0}>
            <Button
              variant="outlined"
              size="small"
              sx={{ border: '1px solid #D0D5DD', borderRadius: '8px', paddingX: '18px', paddingY: '10px', color: '#101828', fontWeight: '500', fontSize: '16px' }}
              onClick={(e) => {
                e.stopPropagation();
                clearInput();
                if (isNewThread) {
                  onCloseDrawer();
                }
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={addButtonDisabled}
              variant="contained"
              color="primary"
              size="small"
              style={{ marginLeft: 8 }}
              sx={{
                background: '#2055FF',
                color: 'white',
                font: 'Inter',
                fontWeight: '500',
                fontSize: '16px',
                paddingX: '18px',
                paddingY: '10px',
                borderRadius: '8px',
              }}
              onClick={async () => {
                setAddButtonDisabled(true);
                setIsLoading(true);
                const result = await handleAdd({ title, description, assignee, messageId, isMainMessage });

                if (result === 'Success') {
                  setFocused(false);
                  clearInput();
                } else {
                  toast.error('Could not send message');
                  setIsLoading(false);
                }
              }}
            >
              {!editMode ? 'Create' : 'Edit'}
            </Button>
          </Stack>
        )}
      </FormControl>
    </>
  );
};
